import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { Loader2, Check } from "lucide-react";
import { apiClient } from "../lib/apiClient";


export function ProfileDialog({ open, onOpenChange, user, churches, onUserUpdate }) {
  const [form, setForm] = useState({ name: "", password: "", church_id: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Seed the form whenever the dialog transitions to open. Cannot do this in
  // `onOpenChange` alone because Radix only fires that callback when *it*
  // wants to change the state (trigger/Esc/outside-click) — not when the
  // parent sets `open={true}` externally (e.g. via a menu item handler).
  // Without this effect the dialog appears with every field blank.
  useEffect(() => {
    if (open) {
      setForm({
        name: user?.name || "",
        password: "",
        church_id: user?.church_id || "",
      });
      setSuccess(false);
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      const payload = {};
      if (form.name.trim() && form.name.trim() !== user?.name) {
        payload.name = form.name.trim();
      }
      if (form.password) {
        payload.password = form.password;
      }
      const newChurchId = form.church_id || null;
      if (newChurchId !== (user?.church_id || null)) {
        payload.church_id = newChurchId;
      }
      if (Object.keys(payload).length === 0) {
        onOpenChange(false);
        return;
      }
      const res = await apiClient.patch("authProfile", payload);
      const updatedUser = res.data;
      localStorage.setItem("user", JSON.stringify(updatedUser));
      onUserUpdate(updatedUser);
      setSuccess(true);
      setTimeout(() => { onOpenChange(false); setSuccess(false); }, 1200);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif">Edit Profile</DialogTitle>
          <DialogDescription>Update your personal information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profileName">Name</Label>
            <Input
              id="profileName"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="bg-stone-light"
              data-testid="profile-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profilePassword">New Password</Label>
            <Input
              id="profilePassword"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Leave blank to keep current"
              className="bg-stone-light"
              minLength={6}
              data-testid="profile-password-input"
            />
            <p className="text-xs text-stone-dark/40">Minimum 6 characters. Leave blank to keep your current password.</p>
          </div>
          {churches.length > 0 && (
            <div className="space-y-2">
              <Label>Church</Label>
              <Select
                value={form.church_id || "none"}
                onValueChange={(value) => setForm({ ...form, church_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="bg-stone-light" data-testid="profile-church-select">
                  <SelectValue placeholder="No church" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No church association</SelectItem>
                  {churches.map((church) => (
                    <SelectItem key={church.id} value={church.id}>
                      {church.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-600" data-testid="profile-success">
              <Check className="w-4 h-4" />
              Profile updated successfully!
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-sage hover:bg-sage-dark text-white rounded-full"
              disabled={loading}
              data-testid="profile-save-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
